import {
  MRCOG_DESCRIPTION,
  MrcogFilterFields,
  getMrcogFilterOperators,
  renderMrcogCell,
} from "../pricetool/customRenderCellComponents/MrcogRollupCell";
import { renderRestockStatusCell } from "../pricetool/customRenderCellComponents/RestockStatusCell";
import {
  MAVCG_DESCRIPTION,
  MavcgFilterFields,
  getMavcgFilterOperators,
  renderMavcgCell,
} from "../pricetool/customRenderCellComponents/MavcgRollupCell";
import {
  MASTER_INVENTORY_DESCRIPTION,
  MasterInventoryFilterFields,
  getMasterInventoryFilterOperators,
  renderMasterInventoryCell,
} from "../pricetool/customRenderCellComponents/InventoryMetricsCell";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { productContactNames } from "../../../apis/productContacts";
import { ExtendedGridColDef } from "../../../components/datagrid/overrides";
import { trimWithEllipses } from "../../../components/datagrid/formatting";
import { renderDate, parseDateUtc } from "../../../formatting/dateFormat";
import BigNumber from "bignumber.js";
import {
  getMultiSelectFilterOperators,
  MultiSelectCell,
  MultiSelectEditCell,
} from "../../../components/datagrid/MultiSelectCell";

export const columns: ExtendedGridColDef[] = [
  {
    field: "product.product_id",
    headerName: "Product ID",
    type: "number",
    valueFormatter: (value) => value ?? "",
    valueGetter: (value, row) => row.product?.product_id,
  },
  {
    field: "product.product_title",
    headerName: "Title",
    valueGetter: (value, row) => row.product?.product_title,
    editable: true,
  },
  {
    field: "product_asin_list_view.asin_list",
    headerName: "ASINs",
    description: "All Amazon ASINs associated with this product.",
    width: 300,
    disableAutosize: true,
    renderCell: (params: GridRenderCellParams) =>
      trimWithEllipses(params.value, 250),
    valueGetter: (value, row) => row.product_asin_list_view?.asin_list,
  },
  {
    field: "product_families_view.families",
    headerName: "Families",
    description: "Product families this product is a member of.",
    width: 250,
    disableAutosize: true,
    valueGetter: (value, row) => row.product_families_view?.families,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={params.value}
        >
          {params.value}
        </div>
      );
    },
  },
  {
    field: "media_types",
    headerName: "Media Types",
    description:
      "The media types in this product (Vinyl, Book, CD, DVD, Bluray, 4K)",
    type: "custom",
    valueGetter: (value, row) => row.media_types,
    valueFormatter: (value: { label: string }[] | null | undefined) =>
      Array.isArray(value) ? value.map((v) => v.label).join(", ") : value ?? "",
    renderCell: (params: GridRenderCellParams) => (
      <MultiSelectCell {...params} />
    ),
    renderEditCell: (params) => <MultiSelectEditCell {...params} />,
    filterOperators: getMultiSelectFilterOperators("media_types"),
    editable: true,
  },
  {
    field: "product.product_mark",
    headerName: "Product Mark",
    description:
      "Identifier for parents of a collection (.amx / .rmx / .mx / .bmx)",
    valueGetter: (value, row) => row.product?.product_mark,
    editable: true,
  },
  {
    field: "product.assembly_part_number",
    headerName: "Part Number",
    description: "Parts required to assemble this product",
    valueGetter: (value, row) => row.product?.assembly_part_number,
  },
  {
    field: "product.component_count",
    headerName: "Part Qty",
    description: "How many components are in this product",
    type: "number",
    valueGetter: (value, row) => row.product?.component_count,
    editable: true,
    editType: "inline",
  },
  {
    field: "product.created_at",
    headerName: "Product Created On",
    description: "When this product row was created in YOM's system",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) => parseDateUtc(row.product?.created_at),
  },
  {
    field: "product.created_by",
    headerName: "Created By",
    description: "The user who created this product",
    valueGetter: (value, row) => row.product?.created_by,
  },
  {
    field: "product.product_contact",
    headerName: "Product Contact",
    description: "The team member responsible for this product",
    type: "singleSelect",
    // Loaded from GET /product-contacts; the page's useProductContacts() warms it
    valueOptions: () => productContactNames(),
    valueGetter: (value, row) => row.product?.product_contact,
    editable: true,
  },
  {
    field: "product.master_inventory",
    headerName: "Master Inventory",
    description: MASTER_INVENTORY_DESCRIPTION,
    type: "custom",
    width: 230,
    rollupFilterEditor: MasterInventoryFilterFields,
    filterOperators: getMasterInventoryFilterOperators(),
    valueGetter: (value, row) => row.product?.ats ?? null,
    renderCell: renderMasterInventoryCell,
    sortable: true,
  },
  {
    field: "product.ats",
    headerName: "ATS",
    description:
      "Quantity of product available to ship. Member of the Master Inventory column.",
    type: "number",
    defaultHidden: true,
    valueGetter: (value, row) => row.product?.ats,
  },
  {
    field: "product.ats_last_increase_timestamp",
    headerName: "ATS Increased",
    description: "Timestamp for when the ATS value last increased",
    type: "date",
    renderCell: (params: GridRenderCellParams) => renderDate(params.value),
    valueGetter: (value, row) =>
      row.product?.ats_last_increase_timestamp
        ? new Date(row.product?.ats_last_increase_timestamp * 1000)
        : null,
  },
  {
    field: "product.master_analyst_inv",
    headerName: "MAI",
    description:
      "Master Analyst Inventory. Current inventory on hand + Inventory On Order (ONO) for all UPCs required to fulfill this product. Member of the Master Inventory column.",
    type: "number",
    defaultHidden: true,
    valueGetter: (value, row) => row.product?.master_analyst_inv,
  },
  {
    field: "product.master_quantity_on_order",
    headerName: "MONO",
    description:
      "Master On Order. Current inventory On Order (ONO) for all UPCs required to fulfill this product. Member of the Master Inventory column.",
    type: "number",
    defaultHidden: true,
    valueGetter: (value, row) => row.product?.master_quantity_on_order,
  },
  {
    field: "product.weeks_until_depleted",
    headerName: "WTD",
    description:
      "Weeks to Depletion. How many weeks of stock do we have on hand or purchased to fulfill this product, based on the last 30 days of sales?",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === 9999999.9) return "No sales.";
      if (data === 9999999.8) return "Inventory error.";
      return data;
    },
    valueGetter: (value, row) => row.product?.weeks_until_depleted,
  },
  {
    field: "product.mavcg",
    headerName: "MAVCG",
    description: MAVCG_DESCRIPTION,
    type: "custom",
    width: 190,
    rollupFilterEditor: MavcgFilterFields,
    filterOperators: getMavcgFilterOperators(),
    valueGetter: (value, row) => row.product?.master_avg_cost_of_goods ?? null,
    renderCell: renderMavcgCell,
    sortable: true,
  },
  {
    field: "product.master_avg_cost_of_goods",
    headerName: "MAVCG (all-time)",
    description:
      "The sum of the average cost of all in-stock components needed to fulfill this product. Member of the MAVCG column.",
    type: "number",
    defaultHidden: true,
    renderCell: (params: GridRenderCellParams) =>
      params.value === null || params.value === undefined
        ? null
        : Number(params.value).toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          }),
    valueGetter: (value, row) => row.product?.master_avg_cost_of_goods,
  },
  {
    field: "product.master_avg_cogs_ninety_day",
    headerName: "MAVCG-H 90",
    description:
      "The master average cost of goods paid for the product using the AVCG-H 90 value (last 90 days). Member of the MAVCG column.",
    type: "number",
    defaultHidden: true,
    renderCell: (params: GridRenderCellParams) =>
      params.value === null || params.value === undefined
        ? null
        : Number(params.value).toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          }),
    valueGetter: (value, row) => row.product?.master_avg_cogs_ninety_day,
  },
  {
    field: "product.last_ninety_day_avg_cogs",
    headerName: "AVCG-H 90",
    description:
      "The average (mean) cost paid for the product in our warehouse in the last 90 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.last_ninety_day_avg_cogs,
  },
  {
    field: "product.mrcog",
    headerName: "MRCOG",
    description: MRCOG_DESCRIPTION,
    type: "custom",
    width: 190,
    rollupFilterEditor: MrcogFilterFields,
    filterOperators: getMrcogFilterOperators(),
    valueGetter: (value, row) => row.product?.replacement_cost_dist ?? null,
    renderCell: renderMrcogCell,
    sortable: true,
  },
  {
    field: "product.replacement_cost_dist",
    headerName: "MRCOG-IS",
    description:
      "In stock with a distributor: the lowest cost of purchasing all of the supply inventory required to fulfill this selling option from Alliance-family or DealsAreUs prices with quantity. Member of the MRCOG column.",
    type: "number",
    defaultHidden: true,
    valueGetter: (value, row) => row.product?.replacement_cost_dist,
  },
  {
    field: "product.replacement_cost",
    headerName: "MRCOG-P",
    description:
      "Purchasable from any supplier data: the lowest possible cost of purchasing all of the supply inventory required to fulfill this selling option, from any supplier, dated or not. Member of the MRCOG column.",
    type: "number",
    defaultHidden: true,
    valueGetter: (value, row) => row.product?.replacement_cost,
  },
  {
    field: "product.rcog_dist_observed_at",
    headerName: "MRCOG-IS Data Observed",
    description:
      "Oldest supplier observation among the parts behind MRCOG-IS (blank when a part is undated)",
    type: "dateTime",
    defaultHidden: true,
    valueGetter: (value, row) =>
      row.product?.rcog_dist_observed_at
        ? new Date(row.product.rcog_dist_observed_at)
        : null,
    renderCell: (params: GridRenderCellParams) =>
      renderDate(params.row.product?.rcog_dist_observed_at),
  },
  {
    field: "product.rcog_observed_at",
    headerName: "MRCOG-P Data Observed",
    description:
      "Oldest supplier observation among the parts behind MRCOG-P (blank when a part is undated)",
    type: "dateTime",
    defaultHidden: true,
    valueGetter: (value, row) =>
      row.product?.rcog_observed_at
        ? new Date(row.product.rcog_observed_at)
        : null,
    renderCell: (params: GridRenderCellParams) =>
      renderDate(params.row.product?.rcog_observed_at),
  },
  {
    field: "product.rcog_dist_undated_parts",
    headerName: "MRCOG-IS Undated Parts",
    description:
      "Parts behind MRCOG-IS whose supplier price has no observation date",
    type: "number",
    defaultHidden: true,
    valueGetter: (value, row) => row.product?.rcog_dist_undated_parts,
  },
  {
    field: "product.rcog_undated_parts",
    headerName: "MRCOG-P Undated Parts",
    description:
      "Parts behind MRCOG-P whose supplier price has no observation date",
    type: "number",
    defaultHidden: true,
    valueGetter: (value, row) => row.product?.rcog_undated_parts,
  },
  {
    field: "product.master_avg_replacement_cost_diff",
    headerName: "MAVCG - MRCOG-IS",
    description: "MAVCG - MRCOG-IS",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      const formatCurrency = (data: any) =>
        data !== null ? `$${Number(data).toFixed(2)}` : "";
      return formatCurrency(data);
    },
    valueGetter: (value, row) => row.product?.master_avg_replacement_cost_diff,
  },
  {
    field: "product.restock_status_calculated",
    headerName: "Restock (feeds)",
    description:
      "Availability in the Alliance and DealsAreUs feeds only, rolled up over the product's complete-set combinations. IS=In stock, BO=Back order, OP=Not in either feed. See Restock (all suppliers) for every tracked supplier.",
    type: "string",
    renderCell: renderRestockStatusCell("FTP"),
    valueGetter: (value, row) => row.product?.restock_status_calculated,
  },
  {
    field: "product.restock_status_all",
    headerName: "Restock (all suppliers)",
    description:
      "Restock status across every tracked supplier, rolled up over the product's complete-set combinations. IS=In stock somewhere, BO=Backorderable from a distributor, NS=No tracked source, OP=Out of print (Alliance discontinued date or OP override).",
    type: "string",
    renderCell: renderRestockStatusCell("ALL"),
    valueGetter: (value, row) => row.product?.restock_status_all,
  },
  {
    field: "product.restock_all_observed_at",
    headerName: "Restock (all) Data Observed",
    description:
      "Oldest supplier observation supporting the parts behind Restock (all suppliers) when it is IS or BO (blank when a part is undated)",
    type: "dateTime",
    defaultHidden: true,
    valueGetter: (value, row) =>
      row.product?.restock_all_observed_at
        ? new Date(row.product.restock_all_observed_at)
        : null,
    renderCell: (params: GridRenderCellParams) =>
      renderDate(params.row.product?.restock_all_observed_at),
  },
  {
    field: "product.restock_all_undated_parts",
    headerName: "Restock (all) Undated Parts",
    description:
      "Parts behind Restock (all suppliers) whose supporting supplier observation has no date",
    type: "number",
    defaultHidden: true,
    valueGetter: (value, row) => row.product?.restock_all_undated_parts,
  },
  {
    field: "product.30_day_restock_in_stock_count",
    headerName: "30 Day IS Rate",
    description:
      "Total days with restock status = IS in the past 30 days. Reported as # days (%)",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;

      const percentage = new BigNumber(data).dividedBy(30).multipliedBy(100);
      return `${data} (${percentage.toFixed(0)}%)`;
    },
    valueGetter: (value, row) => row.product?.["30_day_restock_in_stock_count"],
  },
  {
    field: "product.total_sales",
    headerName: "Total Sales",
    description:
      "The total units of this product sold on Amazon over its lifetime.",
    type: "number",
    valueGetter: (value, row) => row.product?.total_sales,
  },
  {
    field: "product.365_day_sales",
    headerName: "365 Day Sales",
    description: "Units of this product sold on Amazon over the past 365 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["365_day_sales"],
  },
  {
    field: "product.90_day_sales",
    headerName: "90 Day Sales",
    description: "Units of this product sold on Amazon over the past 90 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["90_day_sales"],
  },
  {
    field: "product.30_day_sales",
    headerName: "30 Day Sales",
    description: "Units of this product sold on Amazon over the past 30 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["30_day_sales"],
  },
  {
    field: "product_30_day_profit_view.30_day_profit",
    headerName: "30 Day Profit",
    description:
      "Sum of 30-day profit across all Amazon listings associated with this product.",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === null || data === undefined) return null;
      return `$${Number(data).toFixed(2)}`;
    },
    valueGetter: (value, row) =>
      row.product_30_day_profit_view?.["30_day_profit"],
  },
  {
    field: "product.7_day_sales",
    headerName: "7 Day Sales",
    description: "Units of this product sold on Amazon over the past 7 days.",
    type: "number",
    valueGetter: (value, row) => row.product?.["7_day_sales"],
  },
  {
    field: "product.sales_365_90_30_7",
    headerName: "All Sales",
    description: "Sales in the following format: 365 - 90 - 30 - 7.",
    valueGetter: (value, row) => row.product?.sales_365_90_30_7,
  },
  {
    field: "product.short_term_demand_trend_percentage",
    headerName: "ST Demand %",
    description: "Listing Demand Short Term Demand Trend Percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;
      return `${data}%`;
    },
    valueGetter: (value, row) =>
      row.product?.short_term_demand_trend_percentage,
  },
  {
    field: "product.medium_term_demand_trend_percentage",
    headerName: "MT Demand %",
    description: "Listing Demand Medium Term Demand Trend Percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;
      return `${data}%`;
    },
    valueGetter: (value, row) =>
      row.product?.medium_term_demand_trend_percentage,
  },
  {
    field: "product.yom_purchase_rec",
    headerName: "YPR",
    description: "Yom's purchase recommendation",
    type: "number",
    valueGetter: (value, row) => row.product?.yom_purchase_rec,
  },
];
